import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle, ArrowRight, BadgeCheck, Check, CreditCard, Lock, Minus, Users,
} from 'lucide-react';
import api from '../config';
import { useBilling } from '../context/BillingContext';
import {
  FEATURE_LABEL, PLAN_BLURB, PLAN_LABEL, cycleLabel, money, planPrice, shortDate, statusLabel, termLabel,
} from '../billingCopy';

function Meter({ label, used, limit, suffix }) {
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(limit, 1)) * 100));
  const tone = pct >= 100 ? 'bg-rose-500' : pct >= 80 ? 'bg-amber-500' : 'bg-brand-500';
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{label}</p>
      <p className="mt-1.5 text-2xl font-bold text-navy-900">
        {used}
        {!unlimited && <span className="text-sm font-medium text-slate-400"> / {limit}</span>}
        {unlimited && <span className="ml-2 text-xs font-medium text-slate-400">no limit</span>}
      </p>
      {!unlimited && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full rounded-full ${tone}`} style={{ width: `${pct}%` }} />
        </div>
      )}
      {suffix && <p className="mt-2 text-xs text-slate-500">{suffix}</p>}
    </div>
  );
}

export default function Billing() {
  const { billing, refresh } = useBilling();
  const [selected, setSelected] = useState(null);
  const [seats, setSeats] = useState(3);
  const [cycle, setCycle] = useState('annual');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [events, setEvents] = useState([]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    api.get('/api/billing/events')
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => setEvents([]));
  }, []);

  const plans = billing?.plans || [];
  const current = billing?.plan;
  const readOnly = Boolean(billing?.read_only);

  // A plan the firm picked on the landing page / sign-up page is pre-selected
  // here so the invoice request starts from their actual choice, not a guess.
  const [intent, setIntent] = useState(null);
  // Card payment config from the server: which plans have a Stripe price.
  const [card, setCard] = useState({ enabled: false, environment: 'off', prices: {} });
  useEffect(() => {
    try {
      const raw = localStorage.getItem('caply_signup_intent');
      if (raw) {
        const parsed = JSON.parse(raw);
        setIntent(parsed);
        if (parsed?.plan) setSelected(parsed.plan);
        if (parsed?.cycle) setCycle(parsed.cycle === 'monthly' ? 'monthly' : 'annual');
        if (parsed?.seats) setSeats(Number(parsed.seats) || 3);
      }
    } catch (err) {
      // A corrupt intent is not worth a broken page: ignore it.
    }
  }, []);

  useEffect(() => {
    api.get('/api/billing/checkout')
      .then(({ data }) => setCard(data))
      .catch(() => setCard({ enabled: false, environment: 'off', prices: {} }));
  }, []);

  // Stripe sends the browser back here; say plainly what happened.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const state = params.get('checkout');
    if (state === 'success') {
      setNotice({
        tone: 'ok',
        message: 'Payment received. Stripe confirms the subscription in a moment and your plan switches on automatically.',
      });
    } else if (state === 'cancelled') {
      setNotice({ tone: 'err', message: 'Checkout was closed before paying. Nothing was charged and your plan is unchanged.' });
    }
    if (state) {
      params.delete('checkout');
      const qs = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
    }
  }, []);

  useEffect(() => {
    if (current && selected === null) {
      const seatFloor = Math.max(current.seat_min || 1, billing?.usage?.seats_used || 1);
      setSeats(seatFloor);
    }
  }, [current, selected, billing]);

  const dismissIntent = () => {
    localStorage.removeItem('caply_signup_intent');
    setIntent(null);
  };

  const chosen = useMemo(() => plans.find((p) => p.id === selected) || null, [plans, selected]);

  const requestPlan = async (planId) => {
    const plan = plans.find((p) => p.id === planId);
    setBusy(true);
    setNotice(null);
    try {
      const { data } = await api.post('/api/billing/plan', {
        plan_id: planId,
        seats: Math.max(seats, plan?.seat_min || 1),
        payment_mode: 'invoice',
        cycle,
      });
      setNotice({ tone: 'ok', message: data.message });
      await refresh();
      const ev = await api.get('/api/billing/events').catch(() => null);
      if (ev) setEvents(ev.data.events || []);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNotice({
        tone: 'err',
        message: (typeof detail === 'object' ? detail?.message : detail) || 'We could not record that request.',
      });
    } finally {
      setBusy(false);
    }
  };

  const subscribe = async (planId) => {
    const plan = plans.find((p) => p.id === planId);
    setBusy(true);
    setNotice(null);
    try {
      const base = window.location.origin;
      const { data } = await api.post('/api/billing/checkout', {
        plan_id: planId,
        seats: Math.max(seats, plan?.seat_min || 1),
        cycle,
        success_url: `${base}/billing?checkout=success`,
        cancel_url: `${base}/billing?checkout=cancelled`,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setNotice({ tone: 'ok', message: 'Subscription updated.' });
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNotice({
        tone: 'err',
        message: (typeof detail === 'object' ? detail?.message : detail) || 'We could not open card checkout.',
      });
    } finally {
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/api/billing/portal', null, {
        params: { return_url: `${window.location.origin}/billing` },
      });
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setNotice({
        tone: 'err',
        message: (typeof detail === 'object' ? detail?.message : detail) || 'Billing portal is not available yet.',
      });
    } finally {
      setBusy(false);
    }
  };

  const cancelPlan = async () => {
    setBusy(true);
    try {
      await api.post('/api/billing/plan/cancel');
      setNotice({ tone: 'ok', message: 'Plan cancelled. The workspace is read only and nothing was deleted.' });
      await refresh();
    } catch {
      setNotice({ tone: 'err', message: 'Only a firm admin can cancel the plan.' });
    } finally {
      setBusy(false);
    }
  };

  if (!billing) {
    return <div className="p-6 text-sm text-slate-500">Loading your plan…</div>;
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      {intent && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3">
          <p className="text-[13px] text-brand-900">
            You picked <strong>{PLAN_LABEL[intent.plan] || intent.plan}</strong> billed{' '}
            {intent.cycle === 'monthly' ? 'monthly' : 'yearly'} when you signed up. It is selected below:
            confirm the invoice whenever you are ready.
          </p>
          <button onClick={dismissIntent} className="text-[12px] font-semibold text-brand-800 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* current plan */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Your plan</p>
            <h2 className="mt-1 flex items-center gap-2 text-2xl font-bold text-navy-900">
              {billing.plan_name}
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                readOnly ? 'bg-rose-100 text-rose-700' : billing.status === 'trialing' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {statusLabel(billing)}
              </span>
            </h2>
            <p className="mt-2 max-w-xl text-sm text-slate-600">
              {PLAN_BLURB[billing.plan_id] || current?.summary}
            </p>
            <p className="mt-3 text-xs text-slate-500">
              {billing.status === 'trialing' && billing.trial_ends_at && (
                <>Trial ends {shortDate(billing.trial_ends_at)}. No card is needed to look around.</>
              )}
              {billing.status === 'active' && billing.current_period_end && (
                <>Renews {shortDate(billing.current_period_end)}. {billing.seats_licensed} seats licensed.</>
              )}
              {billing.status === 'pending_invoice' && billing.invoice_request && (
                <>Invoice {billing.invoice_request.reference} for {money(billing.invoice_request.amount_usd)} a year, {billing.invoice_request.seats} seats. Access continues until it is paid.</>
              )}
              {readOnly && <>Read only: your data is intact and exportable, new work is paused.</>}
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold text-navy-900">{planPrice(current || { price_per_user_month: 0 })}</p>
            {billing.year_price_estimate ? (
              <p className="mt-1 text-xs text-slate-500">≈ {money(billing.year_price_estimate)} a year</p>
            ) : null}
          </div>
        </div>
      </div>

      {/* meters */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Meter label="Deals" used={billing.usage.deals_total} limit={billing.limits.max_deals_total}
               suffix={billing.limits.max_deals_total ? 'Total on this plan' : 'Unlimited'} />
        <Meter label="Companies this month" used={billing.usage.deals_this_month} limit={billing.limits.companies_per_month}
               suffix={billing.limits.companies_per_month ? 'Resets on the 1st' : 'Unlimited'} />
        <Meter label="Reports this month" used={billing.usage.reports_this_month} limit={billing.limits.reports_per_month}
               suffix={billing.limits.reports_per_month ? 'Resets on the 1st' : 'Unlimited'} />
        <Meter label="Seats" used={billing.usage.seats_used} limit={billing.limits.seat_max}
               suffix={billing.limits.seat_max ? 'Included seats' : 'Billed per seat'} />
      </div>

      {notice && (
        <div className={`flex items-start gap-2 rounded-xl border px-4 py-3 text-sm ${
          notice.tone === 'ok' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-800'
        }`}>
          {notice.tone === 'ok' ? <BadgeCheck className="mt-0.5 h-4 w-4" /> : <AlertTriangle className="mt-0.5 h-4 w-4" />}
          <span>{notice.message}</span>
        </div>
      )}

      {/* plans */}
      <div>
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-navy-900">Plans</h3>
            <p className="mt-1 text-sm text-slate-600">
              Per user. Pay for the year and each month costs 20% less than paying monthly.
            </p>
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            {[['annual', 'Yearly'], ['monthly', 'Monthly']].map(([value, label]) => (
              <button
                key={value}
                onClick={() => setCycle(value)}
                className={`rounded-[7px] px-3 py-1.5 text-xs font-semibold transition ${
                  cycle === value ? 'bg-navy-900 text-white' : 'text-slate-500 hover:text-navy-900'
                }`}
              >
                {label}
                {value === 'annual' && (
                  <span className={`ml-1.5 rounded px-1 py-0.5 text-[10px] font-bold ${
                    cycle === 'annual' ? 'bg-brand-500 text-navy-950' : 'bg-brand-100 text-brand-800'
                  }`}>
                    20% off
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {plans.map((plan) => {
            const isCurrent = plan.id === billing.plan_id;
            const emphasised = plan.id === 'deal_team';
            return (
              <div
                key={plan.id}
                className={`flex flex-col rounded-2xl border bg-white p-5 ${
                  isCurrent ? 'border-brand-500 ring-1 ring-brand-300' : emphasised ? 'border-navy-900' : 'border-slate-200'
                }`}
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-bold uppercase tracking-wide text-navy-900">{plan.name}</h4>
                  {isCurrent && <span className="rounded-full bg-brand-100 px-2 py-0.5 text-[10px] font-bold uppercase text-brand-800">Current</span>}
                  {emphasised && !isCurrent && <span className="rounded-full bg-navy-900 px-2 py-0.5 text-[10px] font-bold uppercase text-white">Popular</span>}
                </div>
                <p className="mt-2 text-lg font-bold text-navy-900">{planPrice(plan, cycle)}</p>
                <p className="mt-1 text-xs text-slate-500">
                  {plan.price_per_user_month ? `Minimum ${plan.seat_min} seats, ${cycleLabel(cycle)}` : `From ${plan.seat_min} seat`}
                </p>
                <p className="mt-3 text-xs leading-relaxed text-slate-600">{PLAN_BLURB[plan.id] || plan.summary}</p>

                <ul className="mt-4 space-y-1.5 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                    {plan.companies_per_month ? `${plan.companies_per_month} companies a month` : 'Unlimited companies'}
                  </li>
                  {plan.features.slice(0, 6).map((f) => (
                    <li key={f} className="flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5 text-emerald-600" />
                      {FEATURE_LABEL[f] || f}
                    </li>
                  ))}
                  {plan.features.length > 6 && (
                    <li className="flex items-center gap-1.5 text-slate-400">
                      <Minus className="h-3.5 w-3.5" />and {plan.features.length - 6} more
                    </li>
                  )}
                </ul>

                <div className="mt-auto pt-4">
                  {isCurrent ? (
                    <button disabled className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-400">
                      Your plan
                    </button>
                  ) : (
                    <button
                      onClick={() => { setSelected(plan.id); setSeats(Math.max(plan.seat_min, billing.usage.seats_used)); }}
                      className={`w-full rounded-lg px-3 py-2 text-xs font-semibold transition ${
                        emphasised ? 'bg-brand-600 text-navy-950 hover:bg-brand-700' : 'bg-navy-900 text-white hover:bg-navy-800'
                      }`}
                    >
                      {readOnly ? 'Restore access' : 'Choose ' + plan.name}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* checkout panel */}
      {chosen && (
        <div className="rounded-2xl border border-slate-200 bg-white p-6">
          <h3 className="text-base font-bold text-navy-900">Switch to {chosen.name}</h3>
          <div className="mt-4 flex flex-wrap items-end gap-5">
            <div>
              <label className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">Seats</label>
              <div className="mt-1 flex items-center gap-2">
                <button
                  onClick={() => setSeats((s) => Math.max(chosen.seat_min, s - 1))}
                  className="h-8 w-8 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                >
                  −
                </button>
                <input
                  type="number"
                  value={seats}
                  min={chosen.seat_min}
                  onChange={(e) => setSeats(Math.max(chosen.seat_min, Number(e.target.value) || chosen.seat_min))}
                  className="w-20 rounded-lg border border-slate-300 px-2 py-1.5 text-center text-sm font-semibold text-navy-900"
                />
                <button
                  onClick={() => setSeats((s) => s + 1)}
                  className="h-8 w-8 rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50"
                >
                  +
                </button>
                <span className="ml-1 flex items-center gap-1 text-xs text-slate-500">
                  <Users className="h-3.5 w-3.5" /> {billing.usage.seats_used} in use now
                </span>
              </div>
            </div>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-widest text-slate-400">{termLabel(cycle)}</p>
              <p className="mt-1 text-xl font-bold text-navy-900">
                {chosen.price_per_user_month
                  ? money(
                    (cycle === 'monthly' ? chosen.price_per_user_month_monthly : chosen.price_per_user_month)
                    * (cycle === 'monthly' ? 1 : 12)
                    * Math.max(seats, chosen.seat_min)
                  )
                  : 'Quoted with you'}
              </p>
              {chosen.price_per_user_month && (
                <p className="mt-0.5 text-[11px] text-slate-500">
                  {money(
                    (cycle === 'monthly' ? chosen.price_per_user_month_monthly : chosen.price_per_user_month)
                    * Math.max(seats, chosen.seat_min)
                  )} per user per month, {cycleLabel(cycle)}
                </p>
              )}
            </div>
            <div className="ml-auto flex flex-wrap gap-2">
              {card.prices?.[chosen.id] && card.enabled && (
                <button
                  disabled={busy}
                  onClick={() => subscribe(chosen.id)}
                  className="rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50"
                >
                  {busy ? 'Opening checkout…' : 'Subscribe by card'}
                </button>
              )}
              <button
                disabled={busy || billing.plan_id === chosen.id}
                onClick={() => requestPlan(chosen.id)}
                className={
                  card.prices?.[chosen.id] && card.enabled
                    ? 'rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50'
                    : 'rounded-lg bg-navy-900 px-4 py-2 text-sm font-semibold text-white hover:bg-navy-800 disabled:opacity-50'
                }
              >
                Request invoice
              </button>
              <button
                onClick={() => setSelected(null)}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50"
              >
                Cancel
              </button>
            </div>
          </div>
          <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
            {card.enabled ? (
              <><CreditCard className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Pay by card and the subscription renews on its own; seats are billed per user.
                {card.environment === 'test' && (
                  <span className="ml-1 rounded bg-amber-100 px-1.5 py-0.5 font-semibold text-amber-800">
                    Stripe test mode: no card is really charged
                  </span>
                )}
              </>
            ) : (
              <><Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                Plans are invoiced today: we send the invoice, nothing is charged until it is paid, and your access
                continues while it is in flight.
              </>
            )}
          </p>
        </div>
      )}

      {/* history */}
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-6 py-4">
          <h3 className="text-base font-bold text-navy-900">History</h3>
        </div>
        {events.length === 0 ? (
          <p className="px-6 py-5 text-sm text-slate-500">Nothing billed yet.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {events.map((ev) => (
              <li key={ev.id} className="flex flex-wrap items-center gap-3 px-6 py-3 text-sm">
                <span className="font-medium text-navy-900">
                  {ev.type === 'trial_started' && 'Trial started'}
                  {ev.type === 'invoice_requested' && `Invoice requested for ${PLAN_LABEL[ev.plan_id] || ev.plan_id}`}
                  {ev.type === 'plan_activated' && `${PLAN_LABEL[ev.plan_id] || ev.plan_id} activated`}
                  {ev.type === 'plan_canceled' && 'Plan cancelled'}
                  {!['trial_started', 'invoice_requested', 'plan_activated', 'plan_canceled'].includes(ev.type) && ev.type}
                </span>
                <span className="text-slate-500">{shortDate(ev.created_at)}</span>
                {ev.seats && <span className="text-slate-500">{ev.seats} seats</span>}
                {ev.amount_usd ? <span className="ml-auto font-semibold text-navy-900">{money(ev.amount_usd)} / yr</span> : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-6 py-4 text-sm text-slate-600">
        <span>
          Need something custom: a second fund, private networking, a security review?
          Write to <a className="font-semibold text-navy-900 underline" href="mailto:sales@caply4pe.com">sales@caply4pe.com</a>.
        </span>
        {billing.stripe_subscription_id && card.enabled && (
          <button onClick={openPortal} disabled={busy} className="text-xs font-semibold text-navy-900 underline">
            Manage card and invoices
          </button>
        )}
        {!readOnly && billing.plan_id !== 'trial' && billing.plan_id !== 'internal' && (
          <button onClick={cancelPlan} disabled={busy} className="text-xs font-medium text-slate-500 underline hover:text-rose-600">
            Cancel plan
          </button>
        )}
        <Link to="/settings" className="inline-flex items-center gap-1 text-xs font-semibold text-navy-900 hover:underline">
          Manage team <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
}
